'use client'

import {useCallback, useEffect, useMemo, useRef, useState, type MouseEvent} from 'react'
import {BellOff, ChevronDown, Pin, Search, StickyNote} from 'lucide-react'
import type {Account, ChatMessage, Contact, ConversationSummary} from '@/types'
import {apiDelete, apiGet, apiPost} from '@/lib/api'
import {displayName, filterContacts, formatMessageTime} from '@/lib/utils'
import {LazyAvatar} from '@/components/LazyAvatar'
import {APP_MESSAGE_EVENT} from '@/hooks/useMessageNotifications'
import type {AccountItem} from '@/hooks/useAccounts'
import {CONVERSATION_PIN_CHANGE_EVENT, type ConversationPinChangeDetail} from '@/lib/conversation-events'

type ChatListProps = {
    contacts: Contact[]
    account: Account | null
    accounts?: AccountItem[]
    layout?: 'single' | 'multi'
    activeAccountWxid?: string
    activeConversationWxid: string
    unreadCounts: Record<string, number>
    accountUnreadCounts?: Record<string, number>
    onConversationRead: (wxid: string) => void
    onAccountReadAll?: () => void
    onSelect: (wxid: string, accountWxid?: string) => void
}

type ConversationRow = {
    accountWxid: string
    conversationWxid: string
    name: string
    avatar: string
    lastContent: string
    lastDirection: 'in' | 'out'
    lastMessageAt: number
    messageCount: number
    unreadCount: number
    pinned: boolean
    pinnedAt: number
    muted: boolean
    chatNote: string
}

type ConversationGroup = {
    account: AccountItem
    rows: ConversationRow[]
}

type ContextMenuState = {
    visible: boolean
    x: number
    y: number
    row: ConversationRow | null
}

function sortRows(a: ConversationRow, b: ConversationRow) {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    if (a.pinned && b.pinned && a.pinnedAt !== b.pinnedAt) return b.pinnedAt - a.pinnedAt
    if (b.lastMessageAt !== a.lastMessageAt) return b.lastMessageAt - a.lastMessageAt
    return a.name.localeCompare(b.name)
}

function upsertConversationSummary(
    current: ConversationSummary[],
    message: ChatMessage,
): ConversationSummary[] {
    const previous = current.find(
        (item) => item.conversationWxid === message.conversationWxid,
    )
    const next = current.filter(
        (item) => item.conversationWxid !== message.conversationWxid,
    )
    next.unshift({
        conversationWxid: message.conversationWxid,
        lastContent: message.content,
        lastDirection: message.direction,
        lastMessageAt: message.createdAt,
        messageCount: previous ? previous.messageCount + 1 : 1,
        pinned: previous?.pinned ?? false,
        pinnedAt: previous?.pinnedAt,
        chatNote: previous?.chatNote || '',
    })
    return next.sort(sortConversationSummaries)
}

function sortConversationSummaries(a: ConversationSummary, b: ConversationSummary) {
    if ((a.pinned === true) !== (b.pinned === true)) return a.pinned ? -1 : 1
    if (a.pinned && b.pinned && (a.pinnedAt || 0) !== (b.pinnedAt || 0)) {
        return (b.pinnedAt || 0) - (a.pinnedAt || 0)
    }
    return b.lastMessageAt - a.lastMessageAt
}

export function ChatList({
    contacts,
    account,
    accounts = [],
    layout = 'single',
    activeAccountWxid,
    activeConversationWxid,
    unreadCounts,
    accountUnreadCounts = {},
    onConversationRead,
    onAccountReadAll,
    onSelect,
}: ChatListProps) {
    const [query, setQuery] = useState('')
    const [searchFocused, setSearchFocused] = useState(false)
    const [conversations, setConversations] = useState<ConversationSummary[]>([])
    const [conversationsByAccount, setConversationsByAccount] = useState<Record<string, ConversationSummary[]>>({})
    const [collapsedAccounts, setCollapsedAccounts] = useState<Record<string, boolean>>({})
    const [readAllBubbleWxid, setReadAllBubbleWxid] = useState('')
    const [clearedAccountUnread, setClearedAccountUnread] = useState<Record<string, boolean>>({})
    const [hydratedGroups, setHydratedGroups] = useState<Record<string, Contact>>({})
    const [mutedOverrides, setMutedOverrides] = useState<Record<string, boolean>>({})
    const [pinOverrides, setPinOverrides] = useState<Record<string, {pinned: boolean; pinnedAt: number}>>({})
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({
        visible: false,
        x: 0,
        y: 0,
        row: null,
    })
    const fetchedRef = useRef<Set<string>>(new Set())
    const onlineAccounts = useMemo(
        () => accounts.filter((item) => item.status === 'online'),
        [accounts],
    )

    const contactMap = useMemo(
        () => new Map(contacts.map((contact) => [contact.wxid, contact])),
        [contacts],
    )

    const resolveContact = useCallback(
        (wxid: string): Contact | undefined => contactMap.get(wxid) || hydratedGroups[wxid],
        [contactMap, hydratedGroups],
    )

    const fetchConversations = useCallback(async () => {
        if (!account) {
            setConversations([])
            return
        }
        try {
            const response = await apiGet<{
                success: boolean
                conversations: ConversationSummary[]
            }>('/conversations?wxid=' + encodeURIComponent(account.wxid))
            setConversations(response.conversations || [])
        } catch {
            setConversations([])
        }
    }, [account])

    useEffect(() => {
        if (!account) {
            setConversations([])
            fetchedRef.current = new Set()
            setHydratedGroups({})
            return
        }
        let cancelled = false
        ;(async () => {
            try {
                const response = await apiGet<{
                    success: boolean
                    conversations: ConversationSummary[]
                }>('/conversations?wxid=' + encodeURIComponent(account.wxid))
                if (!cancelled) setConversations(response.conversations || [])
            } catch {
                if (!cancelled) setConversations([])
            }
        })()
        return () => {
            cancelled = true
        }
    }, [account])

    useEffect(() => {
        if (layout !== 'multi' || !onlineAccounts.length) {
            setConversationsByAccount((current) => (
                Object.keys(current).length ? {} : current
            ))
            return
        }
        const accountsToFetch = onlineAccounts.filter((item) => {
            if (item.wxid === account?.wxid) return false
            if (conversationsByAccount[item.wxid]) return false
            const collapsed = collapsedAccounts[item.wxid] ?? item.wxid !== account?.wxid
            return !collapsed
        })
        if (!accountsToFetch.length) return
        let cancelled = false
        ;(async () => {
            const entries = await Promise.all(
                accountsToFetch.map(async (item) => {
                    try {
                        const response = await apiGet<{
                            success: boolean
                            conversations: ConversationSummary[]
                        }>('/conversations?wxid=' + encodeURIComponent(item.wxid), {silent: true})
                        return [item.wxid, response.conversations || []] as const
                    } catch {
                        return [item.wxid, []] as const
                    }
                }),
            )
            if (!cancelled) {
                setConversationsByAccount((current) => ({...current, ...Object.fromEntries(entries)}))
            }
        })()
        return () => {
            cancelled = true
        }
    }, [account?.wxid, collapsedAccounts, conversationsByAccount, layout, onlineAccounts])

    useEffect(() => {
        if (!account) return
        const sourceConversations = layout === 'multi'
            ? Object.values(conversationsByAccount).flat()
            : conversations
        const missing = sourceConversations
            .map((conversation) => conversation.conversationWxid)
            .filter(
                (wxid) =>
                    wxid.endsWith('@chatroom') &&
                    !contactMap.has(wxid) &&
                    !hydratedGroups[wxid] &&
                    !fetchedRef.current.has(wxid),
            )
        if (!missing.length) return
        missing.forEach((wxid) => fetchedRef.current.add(wxid))
        let cancelled = false
        ;(async () => {
            try {
                const response = await apiPost<{
                    success: boolean
                    contacts?: Contact[]
                    message?: string
                }>('/group-info', {wxid: account.wxid, qids: missing})
                if (cancelled) return
                if (response.success && response.contacts?.length) {
                    setHydratedGroups((current) => {
                        const next = {...current}
                        for (const contact of response.contacts!) {
                            next[contact.wxid] = contact
                        }
                        return next
                    })
                }
            } catch (error) {
                console.warn('group-info fetch failed', error)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [account, conversations, conversationsByAccount, contactMap, hydratedGroups, layout])

    useEffect(() => {
        const onMessage = (event: Event) => {
            const message = (event as CustomEvent<ChatMessage>).detail
            if (!message?.conversationWxid) return
            const ownerWxid = message.accountWxid || account?.wxid || ''
            if (ownerWxid === account?.wxid) {
                setConversations((current) => upsertConversationSummary(current, message))
            }
            if (ownerWxid) {
                setConversationsByAccount((current) => ({
                    ...current,
                    [ownerWxid]: upsertConversationSummary(current[ownerWxid] || [], message),
                }))
            }
        }
        window.addEventListener(APP_MESSAGE_EVENT, onMessage)
        return () => {
            window.removeEventListener(APP_MESSAGE_EVENT, onMessage)
        }
    }, [account?.wxid])

    useEffect(() => {
        const onPinChange = (event: Event) => {
            const detail = (event as CustomEvent<ConversationPinChangeDetail>).detail
            if (!detail?.accountWxid || !detail.conversationWxid) return
            const pinnedAt = detail.pinned ? detail.pinnedAt || Date.now() : 0
            const override = {pinned: detail.pinned, pinnedAt}
            const patch = (item: ConversationSummary): ConversationSummary => (
                item.conversationWxid === detail.conversationWxid
                    ? {...item, pinned: detail.pinned, pinnedAt: detail.pinned ? pinnedAt : undefined}
                    : item
            )

            setPinOverrides((current) => ({
                ...current,
                [`${detail.accountWxid}:${detail.conversationWxid}`]: override,
            }))
            if (detail.accountWxid === account?.wxid) {
                setConversations((current) => current.map(patch))
            }
            setConversationsByAccount((current) => ({
                ...current,
                [detail.accountWxid]: (current[detail.accountWxid] || []).map(patch),
            }))
        }
        window.addEventListener(CONVERSATION_PIN_CHANGE_EVENT, onPinChange)
        return () => window.removeEventListener(CONVERSATION_PIN_CHANGE_EVENT, onPinChange)
    }, [account?.wxid])

    useEffect(() => {
        if (!activeConversationWxid) return
        onConversationRead(activeConversationWxid)
    }, [activeConversationWxid, onConversationRead])

    useEffect(() => {
        if (!contextMenu.visible) return
        const close = () => setContextMenu({visible: false, x: 0, y: 0, row: null})
        document.addEventListener('click', close)
        document.addEventListener('contextmenu', close)
        return () => {
            document.removeEventListener('click', close)
            document.removeEventListener('contextmenu', close)
        }
    }, [contextMenu.visible])

    useEffect(() => {
        if (!readAllBubbleWxid) return
        const close = () => setReadAllBubbleWxid('')
        document.addEventListener('click', close)
        return () => document.removeEventListener('click', close)
    }, [readAllBubbleWxid])

    useEffect(() => {
        if (!account?.wxid) return
        const total = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0)
        if (total <= 0) return
        setClearedAccountUnread((current) => {
            if (!current[account.wxid]) return current
            const next = {...current}
            delete next[account.wxid]
            return next
        })
    }, [account?.wxid, unreadCounts])

    const buildRows = useCallback((accountWxid: string, source: ConversationSummary[], includeSearchContacts: boolean) => {
        const byWxid = new Map<string, ConversationRow>()
        for (const conversation of source) {
            const contact = resolveContact(conversation.conversationWxid)
            const pinOverride = pinOverrides[`${accountWxid}:${conversation.conversationWxid}`]
            const muted =
                mutedOverrides[`${accountWxid}:${conversation.conversationWxid}`] ??
                mutedOverrides[conversation.conversationWxid] ??
                Boolean(contact?.muted)
            byWxid.set(conversation.conversationWxid, {
                accountWxid,
                conversationWxid: conversation.conversationWxid,
                name: contact ? displayName(contact) : conversation.conversationWxid,
                avatar: contact?.avatar || '',
                lastContent: conversation.lastContent,
                lastDirection: conversation.lastDirection,
                lastMessageAt: conversation.lastMessageAt,
                messageCount: conversation.messageCount,
                unreadCount: accountWxid === account?.wxid ? unreadCounts[conversation.conversationWxid] || 0 : 0,
                pinned: pinOverride?.pinned ?? conversation.pinned === true,
                pinnedAt: pinOverride?.pinnedAt ?? conversation.pinnedAt ?? 0,
                muted,
                chatNote: conversation.chatNote || '',
            })
        }

        if (includeSearchContacts && query.trim()) {
            for (const contact of filterContacts(contacts, query)) {
                if (!byWxid.has(contact.wxid)) {
                    const pinOverride = pinOverrides[`${accountWxid}:${contact.wxid}`]
                    const muted = mutedOverrides[contact.wxid] ?? Boolean(contact.muted)
                    byWxid.set(contact.wxid, {
                        accountWxid,
                        conversationWxid: contact.wxid,
                        name: displayName(contact),
                        avatar: contact.avatar || '',
                        lastContent: '',
                        lastDirection: 'in',
                        lastMessageAt: 0,
                        messageCount: 0,
                        unreadCount: 0,
                        pinned: pinOverride?.pinned ?? false,
                        pinnedAt: pinOverride?.pinnedAt ?? 0,
                        muted,
                        chatNote: contact.chatNote || '',
                    })
                }
            }
        }

        if (includeSearchContacts && activeConversationWxid && !byWxid.has(activeConversationWxid)) {
            const contact = resolveContact(activeConversationWxid)
            const pinOverride = pinOverrides[`${accountWxid}:${activeConversationWxid}`]
            const muted = mutedOverrides[activeConversationWxid] ?? Boolean(contact?.muted)
            byWxid.set(activeConversationWxid, {
                accountWxid,
                conversationWxid: activeConversationWxid,
                name: contact ? displayName(contact) : activeConversationWxid,
                avatar: contact?.avatar || '',
                lastContent: '',
                lastDirection: 'in',
                lastMessageAt: 0,
                messageCount: 0,
                unreadCount: 0,
                pinned: pinOverride?.pinned ?? false,
                pinnedAt: pinOverride?.pinnedAt ?? 0,
                muted,
                chatNote: contact?.chatNote || '',
            })
        }

        const allRows = Array.from(byWxid.values()).sort(sortRows)
        if (!query.trim()) return allRows
        const keyword = query.trim().toLowerCase()
        return allRows.filter(
            (row) =>
                row.name.toLowerCase().includes(keyword) ||
                row.conversationWxid.toLowerCase().includes(keyword),
        )
    }, [account?.wxid, activeConversationWxid, resolveContact, contacts, mutedOverrides, pinOverrides, query, unreadCounts])

    const rows = useMemo(
        () => buildRows(account?.wxid || '', conversations, true),
        [account?.wxid, buildRows, conversations],
    )

    const groupedRows = useMemo<ConversationGroup[]>(() => {
        return onlineAccounts.map((item) => ({
            account: item,
            rows: buildRows(
                item.wxid,
                item.wxid === account?.wxid ? conversations : conversationsByAccount[item.wxid] || [],
                false,
            ),
        }))
    }, [account?.wxid, buildRows, conversations, conversationsByAccount, onlineAccounts])

    function openContextMenu(event: MouseEvent, row: ConversationRow) {
        event.preventDefault()
        event.stopPropagation()
        setContextMenu({visible: true, x: event.clientX, y: event.clientY, row})
    }

    async function togglePinRow(row: ConversationRow) {
        const ownerWxid = row.accountWxid || account?.wxid
        if (!ownerWxid) return
        const desiredPinned = !row.pinned
        setConversations((current) =>
            current.map((item) =>
                item.conversationWxid === row.conversationWxid
                    ? {
                          ...item,
                          pinned: desiredPinned,
                          pinnedAt: desiredPinned ? Date.now() : undefined,
                      }
                    : item,
            ),
        )
        setConversationsByAccount((current) => ({
            ...current,
            [ownerWxid]: (current[ownerWxid] || []).map((item) =>
                item.conversationWxid === row.conversationWxid
                    ? {
                          ...item,
                          pinned: desiredPinned,
                          pinnedAt: desiredPinned ? Date.now() : undefined,
                      }
                    : item,
            ),
        }))
        try {
            const response = await apiPost<{success: boolean}>('/conversations/pin', {
                wxid: ownerWxid,
                conversationWxid: row.conversationWxid,
                pinned: desiredPinned,
            })
            if (!response.success) throw new Error('pin failed')
            await fetchConversations()
        } catch (error) {
            console.warn('pin failed', error)
            await fetchConversations()
        }
    }

    async function toggleMutedRow(row: ConversationRow) {
        const ownerWxid = row.accountWxid || account?.wxid
        if (!ownerWxid) return
        const nextMuted = !row.muted
        setMutedOverrides((current) => ({
            ...current,
            [`${ownerWxid}:${row.conversationWxid}`]: nextMuted,
        }))
        try {
            const response = await apiPost<{success: boolean}>('/contacts/muted', {
                wxid: ownerWxid,
                targetWxid: row.conversationWxid,
                muted: nextMuted ? 1 : 0,
            })
            if (!response.success) throw new Error('mute failed')
        } catch (error) {
            console.warn('mute failed', error)
            setMutedOverrides((current) => ({
                ...current,
                [`${ownerWxid}:${row.conversationWxid}`]: row.muted,
            }))
        }
    }

    async function deleteRow(row: ConversationRow) {
        const ownerWxid = row.accountWxid || account?.wxid
        if (!ownerWxid) return
        if (!window.confirm('确定删除该会话的所有聊天记录吗？此操作不可撤销。')) return
        try {
            await apiDelete<{success: boolean}>(
                '/conversations?wxid=' +
                    encodeURIComponent(ownerWxid) +
                    '&conversationWxid=' +
                    encodeURIComponent(row.conversationWxid),
            )
            setConversations((current) =>
                current.filter((item) => item.conversationWxid !== row.conversationWxid),
            )
            setConversationsByAccount((current) => ({
                ...current,
                [ownerWxid]: (current[ownerWxid] || []).filter((item) => item.conversationWxid !== row.conversationWxid),
            }))
            if (activeConversationWxid === row.conversationWxid) {
                onSelect('')
            }
        } catch (error) {
            console.warn('delete conversation failed', error)
        }
    }

    async function markAccountReadAll(accountWxid: string) {
        if (!accountWxid) return
        setReadAllBubbleWxid('')
        setClearedAccountUnread((current) => ({...current, [accountWxid]: true}))
        if (accountWxid === account?.wxid) {
            onAccountReadAll?.()
        }
        try {
            await apiPost('/unread', {wxid: accountWxid, counts: {}}, {silent: true})
        } catch (error) {
            console.warn('mark account read failed', error)
        }
    }

    const searchWrapperClass = [
        'flex-1 flex items-center gap-2 h-8 px-2 rounded-md bg-white border transition-colors',
        searchFocused ? 'border-accent ring-1 ring-accent/30' : 'border-hair',
    ].join(' ')

    function renderRow(row: ConversationRow) {
        const active = row.accountWxid === (activeAccountWxid || account?.wxid) && row.conversationWxid === activeConversationWxid
        return (
            <button
                key={`${row.accountWxid}:${row.conversationWxid}`}
                type="button"
                onClick={() => onSelect(row.conversationWxid, row.accountWxid)}
                onContextMenu={(event) => openContextMenu(event, row)}
                className={`w-full flex items-center gap-3 px-3 py-3 text-left transition-colors ${
                    active ? 'bg-subActive' : 'hover:bg-subHover'
                }`}
            >
                <span className="relative shrink-0">
                    <LazyAvatar
                        src={row.avatar}
                        seed={row.conversationWxid}
                        className="w-10 h-10 rounded object-cover bg-white"
                    />
                    {row.unreadCount > 0 ? (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-medium flex items-center justify-center">
                            {row.unreadCount > 99 ? '99+' : row.unreadCount}
                        </span>
                    ) : null}
                </span>
                <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                    <span className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-1 min-w-0">
                            {row.pinned ? (
                                <Pin
                                    size={12}
                                    className="text-accent shrink-0"
                                    aria-label="已置顶"
                                />
                            ) : null}
                            {row.chatNote ? (
                                <StickyNote
                                    size={12}
                                    className="text-amber-500 shrink-0"
                                    aria-label="有聊天笔记"
                                />
                            ) : null}
                            {row.muted ? (
                                <BellOff
                                    size={12}
                                    className="text-soft shrink-0"
                                    aria-label="已免打扰"
                                />
                            ) : null}
                            <strong className="truncate text-sm font-medium text-ink">
                                {row.name}
                            </strong>
                        </span>
                        <span className="text-[11px] text-muted shrink-0">
                            {row.lastMessageAt ? formatMessageTime(row.lastMessageAt) : ''}
                        </span>
                    </span>
                    <span className="text-xs text-muted truncate">
                        {row.lastContent
                            ? `${row.lastDirection === 'out' ? '我: ' : ''}${row.lastContent}`
                            : row.conversationWxid}
                    </span>
                </span>
            </button>
        )
    }

    return (
        <div className="flex flex-col h-full bg-sub border-r border-hair">
            <div className="h-[60px] px-3 flex items-center gap-2 border-b border-hair/60 shrink-0">
                <div className={searchWrapperClass}>
                    <Search size={14} className="text-muted shrink-0"/>
                    <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onFocus={() => setSearchFocused(true)}
                        onBlur={() => setSearchFocused(false)}
                        placeholder="搜索"
                        className="flex-1 bg-transparent text-sm text-ink placeholder:text-muted outline-none"
                    />
                </div>
            </div>
            <div className="flex-1 overflow-y-auto">
                {layout === 'multi' ? (
                    groupedRows.length ? (
                        <div className="py-1">
                            {groupedRows.map((group, index) => {
                                const collapsed = collapsedAccounts[group.account.wxid] ?? group.account.wxid !== account?.wxid
                                const unread = clearedAccountUnread[group.account.wxid]
                                    ? 0
                                    : accountUnreadCounts[group.account.wxid] || 0
                                return (
                                    <div key={group.account.wxid}>
                                        {index > 0 ? <div className="mx-3 border-t border-gray-300"/> : null}
                                        <button
                                            type="button"
                                            onClick={() => setCollapsedAccounts((current) => ({
                                                ...current,
                                                [group.account.wxid]: !collapsed,
                                            }))}
                                            className="flex h-11 w-full items-center gap-2 px-3 text-left hover:bg-subHover"
                                        >
                                            <ChevronDown
                                                size={15}
                                                className={`shrink-0 text-soft transition-transform ${collapsed ? '-rotate-90' : ''}`}
                                            />
                                            <LazyAvatar
                                                src={group.account.avatar}
                                                seed={group.account.wxid}
                                                className="h-7 w-7 rounded object-cover bg-white"
                                            />
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-ink">
                                                    {group.account.nickName || group.account.wxid}
                                                </span>
                                                <span className="block truncate text-[11px] text-muted">
                                                    {group.rows.length} 个会话
                                                </span>
                                            </span>
                                            {unread > 0 ? (
                                                <span className="relative">
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            setReadAllBubbleWxid((current) => current === group.account.wxid ? '' : group.account.wxid)
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (event.key !== 'Enter' && event.key !== ' ') return
                                                            event.preventDefault()
                                                            event.stopPropagation()
                                                            setReadAllBubbleWxid((current) => current === group.account.wxid ? '' : group.account.wxid)
                                                        }}
                                                        className="block min-w-[18px] rounded-full bg-red-500 px-1.5 py-0.5 text-center text-[10px] font-medium text-white"
                                                    >
                                                        {unread > 99 ? '99+' : unread}
                                                    </span>
                                                    {readAllBubbleWxid === group.account.wxid ? (
                                                        <span
                                                            className="absolute right-0 top-6 z-popover w-24 rounded-md border border-hair bg-white p-1 shadow-lg"
                                                            onClick={(event) => event.stopPropagation()}
                                                        >
                                                            <button
                                                                type="button"
                                                                onClick={() => void markAccountReadAll(group.account.wxid)}
                                                                className="h-7 w-full rounded text-xs text-ink hover:bg-subHover"
                                                            >
                                                                一键已读
                                                            </button>
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ) : null}
                                        </button>
                                        {!collapsed ? (
                                            group.rows.length ? (
                                                <div>{group.rows.map((row) => renderRow(row))}</div>
                                            ) : (
                                                <div className="px-12 py-5 text-xs text-muted">暂无会话</div>
                                            )
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center text-muted text-sm">
                            暂无在线实例
                        </div>
                    )
                ) : rows.length ? (
                    rows.map((row) => renderRow(row))
                ) : (
                    <div className="h-full flex items-center justify-center text-muted text-sm">
                        暂无会话
                    </div>
                )}
            </div>
            {contextMenu.visible && contextMenu.row ? (
                <div
                    className="fixed z-overlay w-[132px] overflow-hidden rounded-md border border-black/10 bg-white/95 py-1 text-[13px] leading-none shadow-[0_8px_24px_rgba(0,0,0,0.18)] backdrop-blur-xl"
                    style={{top: contextMenu.y, left: contextMenu.x}}
                    onClick={(event) => event.stopPropagation()}
                    onContextMenu={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                >
                    <button
                        type="button"
                        onClick={() => {
                            const row = contextMenu.row!
                            setContextMenu({visible: false, x: 0, y: 0, row: null})
                            void togglePinRow(row)
                        }}
                        className="w-full h-7 px-3 text-left text-ink hover:bg-[#0a84ff] hover:text-white focus:bg-[#0a84ff] focus:text-white focus:outline-none"
                    >
                        {contextMenu.row.pinned ? '取消置顶' : '置顶'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const row = contextMenu.row!
                            setContextMenu({visible: false, x: 0, y: 0, row: null})
                            void toggleMutedRow(row)
                        }}
                        className="w-full h-7 px-3 text-left text-ink hover:bg-[#0a84ff] hover:text-white focus:bg-[#0a84ff] focus:text-white focus:outline-none"
                    >
                        {contextMenu.row.muted ? '取消免打扰' : '免打扰'}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            const row = contextMenu.row!
                            setContextMenu({visible: false, x: 0, y: 0, row: null})
                            void deleteRow(row)
                        }}
                        className="w-full h-7 px-3 text-left text-red-600 hover:bg-[#0a84ff] hover:text-white focus:bg-[#0a84ff] focus:text-white focus:outline-none"
                    >
                        删除聊天
                    </button>
                </div>
            ) : null}
        </div>
    )
}
